import { useRef, type ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { Dialog, DialogContent, DialogTitle } from './shadcn/dialog'

const sizeClasses = {
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
}

// Built on shadcn/ui's Radix-based Dialog, which brings real accessibility
// behavior the old hand-rolled version didn't have (focus trap, ESC to
// close, click-outside to close, a proper portal) -- the open/onClose/
// title/size prop shape is unchanged so every existing call site works
// without modification.
export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  size?: keyof typeof sizeClasses
}) {
  // Radix only returns focus to a <DialogTrigger>, and these dialogs are opened
  // from plain buttons, so focus used to fall back to <body> on close. Remember
  // what had focus when the dialog opened and put it back on close.
  const returnFocusTo = useRef<HTMLElement | null>(null)
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className={cn('flex max-h-[90vh] w-full flex-col gap-0 p-0', sizeClasses[size])}
        onOpenAutoFocus={() => {
          returnFocusTo.current = document.activeElement as HTMLElement | null
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnFocusTo.current?.focus()
        }}
      >
        <div className="shrink-0 border-b border-border px-5 py-4">
          <DialogTitle className="text-sm font-semibold text-foreground">{title}</DialogTitle>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
      </DialogContent>
    </Dialog>
  )
}
